import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { Dialog } from "../Dialog";
import swal from "sweetalert2";
import { AppContext } from "../../contexts/AppContext";
import MediaTable from "../Tables/MediaTable";
import { useDataSets } from "../../contexts/DataSetsContext";
import { saveDataSetItemsWithProgress } from "../../services/data";

// The bar only advances when a batch finishes, which can be seconds apart, so it is animated
// independently: racing stripes keep moving and the elapsed counter ticks every second, so a
// long save never looks stalled.
const stripeSlide = keyframes`
  from { background-position: 0 0; }
  to { background-position: 28px 0; }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const StatusWrapper = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 0;
  background-color: var(--dialog-bg, var(--main-bg));
`;

const SavingLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: var(--main-font-color);
`;

const Spinner = styled.span`
  width: 14px;
  height: 14px;
  flex: 0 0 14px;
  border-radius: 50%;
  border: 2px solid var(--main-border-color);
  border-top-color: var(--button-bg);
  animation: ${spin} 0.7s linear infinite;
`;

const ProgressTrack = styled.div`
  position: relative;
  height: 12px;
  width: 100%;
  border-radius: 6px;
  overflow: hidden;
  background-color: var(--content-bg);
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.12);
  /* Stripes on the unfinished portion, so the bar is visibly alive even before the first batch. */
  background-image: repeating-linear-gradient(
    -45deg,
    rgba(128, 128, 128, 0.22) 0 8px,
    rgba(128, 128, 128, 0) 8px 16px
  );
  background-size: 28px 28px;
  animation: ${stripeSlide} 0.8s linear infinite;
`;

const ProgressFill = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 0%;
  border-radius: 6px;
  background-color: var(--button-bg);
  background-image: repeating-linear-gradient(
    -45deg,
    rgba(255, 255, 255, 0.35) 0 8px,
    rgba(255, 255, 255, 0) 8px 16px
  );
  background-size: 28px 28px;
  animation: ${stripeSlide} 0.8s linear infinite;
  transition: width 0.35s ease;
`;

export const ExistingDataDialog = ({ show, onClose, onSave }) => {
  const { media, fetchMedia } = useContext(AppContext);
  const { addDataSetItems, updatedDataSet, selectedDataSetItems } = useDataSets()
  const [saveText, setSaveText] = useState("Save");
  const [localMedia, setLocalMedia] = useState([]);
  const selectedDataItems = useRef([]);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const lastProgress = useRef(null);
  const progressTextRef = useRef(null);
  const progressBarRef = useRef(null);
  const progressTrackRef = useRef(null);
  const startedAtRef = useRef(0);
  const elapsedTimerRef = useRef(null);

  // Progress is written straight to the DOM rather than held in state: re-rendering this
  // dialog on every tick would re-render the table beneath it, and DataTables clears and
  // redraws its rows whenever its `data` prop changes identity.
  const renderStatus = () => {
    const progress = lastProgress.current;
    if (!progress) return;

    const { saved = 0, total = 0, remaining = 0, failed = 0 } = progress;
    const seconds = startedAtRef.current
      ? Math.max(0, Math.round((Date.now() - startedAtRef.current) / 1000))
      : 0;

    if (progressTextRef.current) {
      if (saved === 0) {
        // Nothing has come back yet — still counts up, so it never looks stalled.
        progressTextRef.current.textContent = `Saving ${total} item(s)… · ${seconds}s`;
      } else {
        const parts = [`Saving ${saved} of ${total}`, `${remaining} remaining`];
        if (failed > 0) parts.push(`${failed} failed`);
        progressTextRef.current.textContent = `${parts.join(" — ")} · ${seconds}s`;
      }
    }

    if (progressBarRef.current) {
      const percent = total > 0 ? Math.min(100, Math.round((saved / total) * 100)) : 0;
      progressBarRef.current.style.width = `${percent}%`;
    }

    if (progressTrackRef.current) {
      progressTrackRef.current.setAttribute("aria-valuenow", String(saved));
    }
  };

  const applyProgress = (progress) => {
    if (!progress) return;
    lastProgress.current = progress;
    renderStatus();
  };

  const startElapsedTimer = () => {
    startedAtRef.current = Date.now();
    if (elapsedTimerRef.current) {
      clearInterval(elapsedTimerRef.current);
    }
    elapsedTimerRef.current = window.setInterval(renderStatus, 1000);
  };

  const stopElapsedTimer = () => {
    if (elapsedTimerRef.current) {
      clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = null;
    }
  };

  // Never leave a timer running if the dialog goes away mid-save.
  useEffect(() => stopElapsedTimer, []);

  // Re-apply after a re-render in case React rewrote the text node.
  useEffect(() => {
    if (saving) {
      renderStatus();
    }
  });

  useEffect(() => {
    console.log("!!! MEDIA DIALOG");
    if (show && (!media || media.length === 0)) {
      fetchMedia();
    } else if (show && media && media.length > 0) {
      setLocalMedia(media);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [media, show]);

  // Items already in the data set are not offered again. This has to be memoized: MediaTable
  // copies its `media` prop into state in an effect, so a fresh array on every render would
  // loop forever.
  const availableMedia = useMemo(() => {
    const existingItemIds = new Set(
      (selectedDataSetItems || [])
        .map((item) => item?.public_id)
        .filter(Boolean)
    );

    return (localMedia || []).filter(
      (item) => !item?.public_id || !existingItemIds.has(item.public_id)
    );
  }, [localMedia, selectedDataSetItems]);

  // Start from a clean selection each time the dialog is opened, so items that have since
  // been added to the data set can't linger as an invisible part of the selection.
  useEffect(() => {
    if (!show) return;
    selectedDataItems.current = [];
    if (window.resetDataItemSelection) {
      window.resetDataItemSelection();
    } else if (window.selectedDataItems) {
      window.selectedDataItems.clear();
    }
  }, [show]);

  const saveDataItems = async () => {
    // Implement your save logic here, e.g., send data to backend or update state
    if (onSave) {
      onSave(selectedDataItems.current);
      return;
    }

    if (savingRef.current) {
      return; // a save is already running; don't start a second one
    }

    console.log("!!! SELECTED DATA ITEMS: ", selectedDataItems.current);
    if (!selectedDataItems.current || selectedDataItems.current.length === 0) {
      swal.fire({
        title: "Error",
        text: "No data items selected.",
        icon: "error",
      });
      setSaveText("Save");
      return;
    }

    const mediaIDs = [...selectedDataItems.current];

    savingRef.current = true;
    lastProgress.current = { saved: 0, total: mediaIDs.length, remaining: mediaIDs.length, failed: 0 };
    setSaveText("Saving...");
    setSaving(true);
    startElapsedTimer();

    let results;
    try {
      results = await saveDataSetItemsWithProgress(
        updatedDataSet.public_id,
        mediaIDs,
        applyProgress
      );
    } catch (error) {
      console.log("SAVE DATA ITEMS ERROR: ", error);
      results = { success: false, message: error?.message || "Failed to save data items." };
    } finally {
      stopElapsedTimer();
      savingRef.current = false;
      setSaving(false);
      setSaveText("Save");
    }

    if (!results.success) {
      swal.fire({
        title: "Error",
        text: results.message || "Failed to save data items.",
        icon: "error",
      });
    } else {
      const items = results.items || [];
      if (items.length > 0) {
        addDataSetItems(items);
      }

      const partial = results.failed && results.failed.length > 0;
      swal.fire({
        title: partial ? "Partly saved" : "Success",
        text: results.message || "Data items saved successfully.",
        icon: partial ? "warning" : "success",
      }).then(() => {
        onClose();
      });
    }
  };

  return (
    <Dialog
      title="Add Data Item"
      subtitle={""}
      show={show}
      onClose={onClose}
      onOK={saveDataItems}
      okText={saveText}
      closeText="Close"
      style={{ width: "90%", maxWidth: "1200px", height: "80vh" }}
    >
      {saving ? (
        <StatusWrapper>
          <SavingLabel>
            <Spinner aria-hidden="true" />
            <span ref={progressTextRef}>Saving…</span>
          </SavingLabel>
          <ProgressTrack
            ref={progressTrackRef}
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={lastProgress.current ? lastProgress.current.total : 0}
            aria-valuenow={0}
            aria-label="Saving data items"
          >
            <ProgressFill ref={progressBarRef} />
          </ProgressTrack>
        </StatusWrapper>
      ) : null}
      {availableMedia.length === 0 ? (
        <p>
          {!localMedia || localMedia.length === 0
            ? "No media items available."
            : "All media items are already in this data set."}
        </p>
      ) : (
        <MediaTable media={availableMedia} hideTitle={true} hideButtons={true} mode="select" onSelect={(items) => {
          console.log("!!! SELECTED ITEMS: ", items);
          selectedDataItems.current = [...items];
          //setSelectedDataItems([...items]);
        }} />
      )}
    </Dialog>
  );
};

export default ExistingDataDialog;
